import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Group } from '@lightspeed/flame/Group';
import { IconPrinters } from '@lightspeed/flame/Icon/Printers';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import { translate } from 'pages/utils/translate';

const StyledModal = styled(Modal)`
  width: 600px;
  max-width: 90%;
`;

interface CancelPreAuthModalProps {
  isOpen: boolean;
  saleNumber: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  onPrintReceipt?: () => void;
  onSuccess?: () => void;
}

type ModalStep = 'confirm' | 'success';

const CancelPreAuthModal: React.FC<CancelPreAuthModalProps> = ({
  isOpen,
  saleNumber,
  onConfirm,
  onClose,
  onPrintReceipt,
  onSuccess,
}) => {
  const [step, setStep] = useState<ModalStep>('confirm');
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset the modal state when it closes or opens
  useEffect(() => {
    if (!isOpen) {
      // Use a timeout to reset after the modal has fully closed (animation complete)
      const timer = setTimeout(() => {
        setStep('confirm');
        setIsProcessing(false);
      }, 300); // 300ms should be enough for most modal close animations
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
      setStep('success');
      // Error handling is done in the parent component
      // If there's an error, the modal should stay on the confirmation step
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // If we're on the success step, call onSuccess before closing
    if (step === 'success' && onSuccess) {
      onSuccess();
    }
    onClose();
  };

  const handlePrintReceipt = () => {
    if (onPrintReceipt) {
      onPrintReceipt();
    }
  };

  return (
    <StyledModal isOpen={isOpen} shouldCloseOnOverlayClick={false}>
      {step === 'confirm' ? (
        <>
          <ModalHeader showCloseButton onCloseClicked={handleClose}>
            <Text fontSize="18px" fontWeight="bold">
              Cancel preauthorization for sale #{saleNumber}?
            </Text>
          </ModalHeader>
          <ModalBody>
            <Text fontSize="14px" lineHeight="1.5">
              {translate(
                "You won't be able to capture the authorized funds after you cancel the preauthorization. This action cannot be undone."
              )}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end">
              <Group>
                <Button onClick={handleClose} disabled={isProcessing}>
                  Close
                </Button>
                <Button
                  variant="danger"
                  fill
                  onClick={handleConfirm}
                  disabled={isProcessing}
                  data-test="cancel-preauth-confirm-btn"
                >
                  {isProcessing ? translate('Cancelling...') : translate('Cancel preauthorization')}
                </Button>
              </Group>
            </Flex>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalHeader showCloseButton onCloseClicked={handleClose}>
            <Text fontSize="18px" fontWeight="bold">
              Preauthorization cancelled
            </Text>
          </ModalHeader>
          <ModalBody>
            <Text fontSize="14px" lineHeight="1.5">
              {translate(
                "The preauthorization has been cancelled. To complete the sale, choose another payment method or cancel the sale if it's no longer needed."
              )}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="space-between" width="100%">
              <Button
                variant="secondary"
                onClick={handlePrintReceipt}
                data-test="print-receipt-btn"
              >
                <Flex alignItems="center">
                  <Box mr={1}>
                    <IconPrinters size="16px" />
                  </Box>
                  Print receipt
                </Flex>
              </Button>
              <Button onClick={handleClose} data-test="close-success-btn">
                Close
              </Button>
            </Flex>
          </ModalFooter>
        </>
      )}
    </StyledModal>
  );
};

export default CancelPreAuthModal;
